<template>
  <OurModal v-model="isShownModal" :title="$t('title.fleaMarket.settingsModal')">
    <SettingsForm
      ref="emailForm"
      v-model="form"
      :manager-processing-norm-hours="managerProcessingNormHours"
      :manager-processing-norm-minutes="managerProcessingNormMinutes"
      @create="createNotice"
      @update="updateNotice"
      @delete="deleteNotice"
      @save-manager-processing-norm="saveManagerProcessingNorm"
    />

    <template #footer-left>
      <OurButton :text="$t('button.close')" @click="closeModal" />
    </template>
  </OurModal>
</template>

<script>
import SettingsForm from "./components/SettingsForm.vue";
import apolloMixin from "@/mixins/apollo.mixin";
import { notificationEmailsAndApplicationSettingsQuery } from "@/views/settings/dealership-applications/settings/gql/queries";
import {
  createNotificationEmailMutation,
  deleteNotificationEmailMutation,
  updateManagerProcessingNormMutation,
  updateNotificationEmailMutation,
} from "@/views/settings/dealership-applications/settings/gql/mutations";

export default {
  name: "SettingsModal",
  components: {
    SettingsForm,
  },
  mixins: [apolloMixin],

  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        emails: [],
      },
      managerProcessingNormHours: 0,
      managerProcessingNormMinutes: 0,
      savedFormSnapshot: null,
    };
  },

  computed: {
    isShownModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },

  watch: {
    isShownModal: "onChangeShownModal",
  },

  methods: {
    async getData() {
      const { notificationEmails, customerApplicationSettings } = await this.$get(
        notificationEmailsAndApplicationSettingsQuery,
        {
          group: "customer",
        }
      );

      this.form.emails = notificationEmails;
      this.managerProcessingNormHours = customerApplicationSettings.managerProcessingNormHours || 0;
      this.managerProcessingNormMinutes =
        customerApplicationSettings.managerProcessingNormMinutes || 0;
      this.savedFormSnapshot = {
        emails: notificationEmails.map((email) => ({ ...email })),
        managerProcessingNormHours: this.managerProcessingNormHours,
        managerProcessingNormMinutes: this.managerProcessingNormMinutes,
      };
    },

    async createNotice(email) {
      this.$refs.emailForm.emitForm();

      if (this.form.isValid) {
        const { createNotificationEmail } = await this.$post(
          createNotificationEmailMutation,
          { group: "customer", email: email },
          {
            delaySuccessNotify: false,
          }
        );

        if (createNotificationEmail.id) await this.getData();
      }
    },

    async updateNotice(object) {
      this.$refs.emailForm.emitForm();

      if (this.form.isValid) {
        const { updateNotificationEmail } = await this.$post(
          updateNotificationEmailMutation,
          { group: "customer", id: object.id, email: object.email },
          {
            delaySuccessNotify: false,
          }
        );

        if (updateNotificationEmail.id) await this.getData();
      }
    },

    async deleteNotice(id) {
      const { deleteNotificationEmail } = await this.$post(
        deleteNotificationEmailMutation,
        { id: id },
        {
          delaySuccessNotify: false,
        }
      );

      if (deleteNotificationEmail.id) await this.getData();
    },

    async saveManagerProcessingNorm({ hours, minutes }) {
      const { updateCustomerApplicationSettings } = await this.$post(
        updateManagerProcessingNormMutation,
        { hours, minutes },
        {
          delaySuccessNotify: false,
        }
      );

      if (updateCustomerApplicationSettings) {
        this.managerProcessingNormHours =
          updateCustomerApplicationSettings.managerProcessingNormHours ?? 0;
        this.managerProcessingNormMinutes =
          updateCustomerApplicationSettings.managerProcessingNormMinutes ?? 0;

        if (this.savedFormSnapshot) {
          this.savedFormSnapshot.managerProcessingNormHours = this.managerProcessingNormHours;
          this.savedFormSnapshot.managerProcessingNormMinutes = this.managerProcessingNormMinutes;
        }
      }
    },

    async onChangeShownModal() {
      if (this.isShownModal) {
        await this.getData();
        this.$refs.emailForm?.resetFormState();

        return;
      }

      this.cleanForm();
    },

    cleanForm() {
      if (!this.savedFormSnapshot) {
        return;
      }

      this.form.emails = this.savedFormSnapshot.emails.map((email) => ({ ...email }));
      this.managerProcessingNormHours = this.savedFormSnapshot.managerProcessingNormHours;
      this.managerProcessingNormMinutes = this.savedFormSnapshot.managerProcessingNormMinutes;
      this.$refs.emailForm?.resetFormState();
    },

    closeModal() {
      this.isShownModal = false;
    },
  },
};
</script>
